import axios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';

import { PERSPECTIVE_TOKEN_KEY } from '../utils/Auth';
import { ErrorMessage } from '../utils/Messages';

import { avisarRecusaDaApi } from '@/lib/idp/recusa-da-api';
import { getIdpAccessToken } from '@/lib/idp/token-store';
import { desistirDaSessaoDoPai, pedirTokenAoPai, tokenDaSessaoDoPai } from '@/lib/sessao-do-pai';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
  /**
   * O access token do IDP com que o `Authorization` saiu, quando saiu com um.
   * Separa, no 401, a sessao do IDP recusada pela API da sessao legada
   * expirada, e deixa ignorar a recusa de um token que o refresh ja trocou.
   */
  tokenDoIdp?: string;
  /**
   * O token do pai (Sapron embutido no portal do franqueado) com que o
   * `Authorization` saiu. No 401, pede token novo ao pai e repete uma vez.
   */
  tokenDoPai?: string;
  /** A requisicao ja e a repeticao com o token novo do pai: recusada de novo, desiste. */
  repetidaComTokenDoPai?: boolean;
}

class ErrorResponse extends Error {
  message: string = '';

  statusCode: number;

  detail: any;

  constructor(message: string, statusCode: number, detail: any) {
    super(message);

    this.message = message;

    this.statusCode = statusCode;

    this.detail = detail;
  }
}

const request = axios.create({
  baseURL: import.meta.env.REACT_APP_URL,
});

request.interceptors.request.use((config: CustomAxiosRequestConfig) => {
  // A repeticao com o token do pai ja passou por aqui: a URL ja e absoluta e
  // leva o `actual_route`, e um segundo `decodeURIComponent` quebraria o `%`
  // que o primeiro tirou de um `%25` (`URIError`).
  if (config.repetidaComTokenDoPai) return config;

  const url = new URL(config.url || '', config.baseURL || '');

  if (window?.location?.pathname) {
    url.searchParams.set('actual_route', String(window.location.pathname));
  }

  return {
    ...config,

    url: decodeURIComponent(url.toString()),

    headers: {
      ...config.headers,
    },
  };
});

function errorResponseDe(error: any): ErrorResponse {
  const responseData = error.response?.data;

  let detail =
    responseData && typeof responseData === 'object'
      ? responseData
      : responseData?.detail || ErrorMessage.default();

  let errorMessage;

  if (
    error.response?.status === 410 &&
    typeof responseData?.errorMessage === 'string' &&
    responseData.errorMessage.trim() !== ''
  ) {
    // `IDP_LOGIN=idp-only`: o backend recusa a senha com o corpo padrao do
    // `custom_exception_handler`, e o texto dele ja diz por onde entrar.
    // Texto vazio nao e mensagem: cai na generica, como sem o campo.
    errorMessage = responseData.errorMessage;
  } else if (responseData?.detail) {
    errorMessage = responseData.detail;
  } else if (responseData?.error) {
    errorMessage = responseData.error;

    detail = errorMessage;
  } else {
    errorMessage = ErrorMessage.default();
  }

  const status = error.response?.status || 0;

  const errorResponse = new ErrorResponse(errorMessage, status, detail);
  // Preserve the request URL so downstream handlers (e.g. global QueryClient
  // toast) can decide whether to react based on the endpoint.
  (errorResponse as ErrorResponse & { requestUrl?: string }).requestUrl =
    error.config?.url ?? error.response?.config?.url ?? '';
  return errorResponse;
}

const NON_REDIRECT_ROUTES = ['/recuperar-senha', '/criarnovasenha', '/parceiros/lancamentos'];

/**
 * 401 com o token do pai: o token pode ter vencido antes de o pai trocar
 * (laptop que dormiu, pai sem responder ao `ready`). Pede um token novo e
 * repete a requisicao uma vez; recusada de novo, ou sem token novo, a sessao
 * do pai acaba e a moldura volta a entrada — o `/login#expired` carregaria
 * um documento que producao serve com `X-Frame-Options: SAMEORIGIN`, e a
 * moldura mostraria a recusa do navegador no lugar do login.
 */
async function repetirComTokenDoPai(config: CustomAxiosRequestConfig, erro: ErrorResponse) {
  if (!config.repetidaComTokenDoPai && config.tokenDoPai !== undefined) {
    const novo = await pedirTokenAoPai(config.tokenDoPai);
    if (novo !== undefined) {
      const repeticao: CustomAxiosRequestConfig = { ...config, repetidaComTokenDoPai: true };
      return request(repeticao);
    }
  }
  desistirDaSessaoDoPai();
  throw erro;
}

request.interceptors.response.use(
  (response) => response,

  (error) => {
    const currentPath = window.location.pathname;

    const isPublicRoute = NON_REDIRECT_ROUTES.some((route) => currentPath.startsWith(route));
    const config = (error.config ?? {}) as CustomAxiosRequestConfig;

    if (error.response?.status === 401 && !isPublicRoute) {
      if (config.tokenDoIdp !== undefined) {
        // Sessao do IDP recusada: o motivo no corpo decide entre sem-vinculo
        // (logout federado e `/sem-conta`) e token recusado (re-login sem
        // tela). O `/login#expired` deixaria a SSO viva e, sem vinculo, o
        // proximo "Entrar com Seazone" voltaria aqui em laco (`recusa-da-api`).
        avisarRecusaDaApi(error.response?.data, config.tokenDoIdp);
      } else if (config.tokenDoPai !== undefined) {
        return repetirComTokenDoPai(config, errorResponseDe(error));
      } else {
        window.location.href = '/login#expired';
      }
    }

    return Promise.reject(errorResponseDe(error));
  },
);

request.interceptors.request.use((config: CustomAxiosRequestConfig) => {
  const headers: AxiosRequestHeaders = {
    'Content-Type': 'application/json',

    ...config.headers,
  };

  const { skipAuth } = config;
  let tokenDoIdp: string | undefined;
  let tokenDoPai: string | undefined;

  if (!skipAuth) {
    // Verifica se está em modo de perspectiva
    const urlParams = new URLSearchParams(window.location.search);
    const isViewMode = urlParams.get('mode') === 'view';

    let token = null;

    if (isViewMode) {
      // Se está em modo de perspectiva, usa o token de perspectiva
      const perspectiveToken = localStorage.getItem(PERSPECTIVE_TOKEN_KEY);
      if (perspectiveToken) {
        token = perspectiveToken;
      }
    }

    // Duas fontes de sessão convivem na virada (IDP_LOGIN=both): a do IDP, que
    // só existe em memória, e a legada, que mora no localStorage. A do IDP tem
    // precedência quando existe — é a sessão que o usuário acabou de abrir; a
    // legada pode ser sobra de um login anterior nesta mesma máquina.
    if (!token) {
      tokenDoIdp = getIdpAccessToken();
      token = tokenDoIdp ?? null;
    }

    // Embutido no portal do franqueado que entrou pelo IDP: o token vem do pai,
    // em memoria, e o pai o troca a cada renovacao — vale o da vez.
    if (!token) {
      tokenDoPai = tokenDaSessaoDoPai();
      token = tokenDoPai ?? null;
    }

    if (!token) {
      const userData = localStorage.getItem('sapron-pms-user');
      if (userData) {
        const { access } = JSON.parse(userData);
        token = access;
      }
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  return {
    ...config,

    ...(tokenDoIdp !== undefined && { tokenDoIdp }),

    ...(tokenDoPai !== undefined && { tokenDoPai }),

    headers: {
      ...config.headers,

      ...headers,
    },
  };
});

export default request;
